import { useMemo } from 'react';
import { MODULES, ersattAvAnbudV2 } from '../data/modules';
import { useAuth } from '../auth/AuthProvider';
import { arSystemadminUtanSimulering } from '../utils/systemadminVy';
import { DOMAIN_SYNONYMS } from '../data/searchSynonyms';

const PREFIX_SCOPES = {
  '@': 'person',
  '#': 'issue',
};

function parseQuery(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return { query: '', scopeFromPrefix: null };
  const first = trimmed[0];
  if (PREFIX_SCOPES[first]) {
    return {
      query: trimmed.slice(1).trim(),
      scopeFromPrefix: PREFIX_SCOPES[first],
    };
  }
  return { query: trimmed, scopeFromPrefix: null };
}

function scoreModule(mod, query) {
  const name = String(mod.label || '').toLowerCase();
  const q = query.toLowerCase().trim();
  if (!q) return { score: 0, matchReason: null };

  let score = 0;
  let matchReason = null;

  if (name === q) {
    score = 1000;
    matchReason = { type: 'name', text: mod.label };
  } else if (name.startsWith(q)) {
    score = 800;
    matchReason = { type: 'name', text: mod.label };
  } else if (name.includes(q)) {
    score = 600;
    matchReason = { type: 'name', text: mod.label };
  }

  const tags = Array.isArray(mod.tags) ? mod.tags : [];
  const matchedTags = tags.filter((t) => String(t).toLowerCase().includes(q));
  if (matchedTags.length > 0) {
    const exact = tags.some((t) => String(t).toLowerCase() === q);
    const tagScore = exact ? 500 : 300;
    if (tagScore > score) {
      score = tagScore;
      matchReason = { type: 'tag', text: matchedTags[0] };
    }
  }

  const synonyms = DOMAIN_SYNONYMS[q] || [];
  for (const syn of synonyms) {
    const synLower = String(syn).toLowerCase();
    if (tags.some((t) => String(t).toLowerCase().includes(synLower))) {
      if (score < 200) {
        score = 200;
        matchReason = { type: 'synonym', text: `via "${syn}"` };
      }
    }
  }

  return { score, matchReason };
}

function applyBoosts(score, mod, { isFavorite, recentRoutes }) {
  if (!score) return 0;
  let boosted = score;
  if (isFavorite && isFavorite(mod.label)) boosted += 50;
  if (recentRoutes && recentRoutes.has(mod.to)) boosted += 30;
  return boosted;
}

export function useGlobalSearch({
  rawQuery,
  scope = null,
  effectiveModuleKeySet = null,
  isSystemAdmin = false,
  isTenantAdmin = false,
  isFavorite,
  recentRoutes,
}) {
  const { user } = useAuth() || {};
  const visaPersonliga = arSystemadminUtanSimulering(user);
  return useMemo(() => {
    const { query, scopeFromPrefix } = parseQuery(rawQuery);
    const activeScope = scopeFromPrefix || scope || null;

    if (!query) {
      return { query: '', activeScope, scopeFromPrefix, groups: [] };
    }

    const includeModules = !activeScope || activeScope === 'module';
    const moduleResults = includeModules
      ? MODULES
          .filter((m) => {
            if (m.systemAdminOnly && !visaPersonliga) return false;
            if (m.adminOnly && !isSystemAdmin) return false;
            if (m.tenantAdminOnly && !isTenantAdmin && !isSystemAdmin) return false;
            if (!m.moduleKey) return true;
            if (!effectiveModuleKeySet) return true;
            return effectiveModuleKeySet.has(m.moduleKey) && !ersattAvAnbudV2(m, effectiveModuleKeySet);
          })
          .map((m) => {
            const { score, matchReason } = scoreModule(m, query);
            const boosted = applyBoosts(score, m, { isFavorite, recentRoutes });
            return { module: m, score: boosted, matchReason };
          })
          .filter((r) => r.score > 0)
          .sort((a, b) => b.score - a.score)
      : [];

    const groups = [];
    if (moduleResults.length > 0) {
      groups.push({ scope: 'module', label: 'Moduler', items: moduleResults, maxVisible: 8 });
    }
    return { query, activeScope, scopeFromPrefix, groups };
  }, [rawQuery, scope, effectiveModuleKeySet, isSystemAdmin, isTenantAdmin, isFavorite, recentRoutes, visaPersonliga]);
}

export function highlightMatch(text, query) {
  const t = String(text || '');
  const q = String(query || '').trim();
  if (!q) return [{ kind: 'text', value: t }];
  const idx = t.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return [{ kind: 'text', value: t }];
  return [
    { kind: 'text', value: t.slice(0, idx) },
    { kind: 'mark', value: t.slice(idx, idx + q.length) },
    { kind: 'text', value: t.slice(idx + q.length) },
  ];
}
